import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, money0, fmtDate, today } from "../format.js";
import {
  Async, Bar, BarList, Card, Chips, Empty, Grid, Ribbon, Sep, Table, Tabs, TopBar, Wrap, useAsync,
} from "../ui.jsx";

const TABS = [
  { key: "summary", label: "Business summary" },
  { key: "ar", label: "A/R ageing" },
  { key: "tax", label: "Sales tax" },
];

const ENDPOINT = { summary: "reports/summary", ar: "reports/ar", tax: "reports/tax" };

export default function Reports() {
  const { settings } = useApp();
  const [tab, setTab] = useState("summary");
  const sym = settings.currency_symbol;

  // Each tab fetches only when it is the active one, so switching stays cheap.
  const main = useAsync(() => api.get(ENDPOINT[tab]), [tab]);
  const revenue = useAsync(() => (tab === "summary" ? api.get("reports/revenue-by-client") : null), [tab]);

  return (
    <>
      <TopBar crumb="Manage" title="Reports" />

      <Wrap>
        <Tabs tabs={TABS} value={tab} onChange={setTab} />

        {tab === "summary" && (
          <Async state={main}>{(d) => <Summary data={d} revenue={revenue} sym={sym} />}</Async>
        )}
        {tab === "ar" && <Async state={main}>{(d) => <Ageing rows={d.rows || []} sym={sym} />}</Async>}
        {tab === "tax" && (
          <Async state={main}>{(d) => <SalesTax data={d} sym={sym} label={settings.tax_label} />}</Async>
        )}
      </Wrap>
    </>
  );
}

/* -------------------------------------------------------------- monthly profit & loss */

/**
 * Revenue and profit by month, this calendar year.
 *
 * Profit is PART of revenue, so the two cannot be two independent stacked pieces —
 * the bar is revenue split into what you kept (profit) and what it cost you
 * (costs). Consequences worth knowing when reading it:
 *
 *   profitable month → bar height IS revenue, capped by the profit segment
 *   losing month     → costs are taller than revenue, so the bar overshoots and
 *                      a hairline marks where revenue actually landed
 *
 * Every number the tooltip shows is also in the Table view, so nothing is
 * reachable by hover alone.
 */
function MonthlyPnL({ months, sym }) {
  const [hover, setHover] = useState(null);
  // Table first: the exact monthly numbers are what gets read most; the chart is
  // one click away for the shape of the year.
  const [view, setView] = useState("table");

  const rows = months.map((m) => {
    const revenue = Number(m.revenue || 0);
    const costs = Number(m.costs || 0);
    return { ...m, revenue, costs, profit: Math.round((revenue - costs) * 100) / 100 };
  });
  const totals = rows.reduce(
    (t, r) => ({ revenue: t.revenue + r.revenue, costs: t.costs + r.costs, profit: t.profit + r.profit }),
    { revenue: 0, costs: 0, profit: 0 }
  );
  const max = Math.max(1, ...rows.map((r) => Math.max(r.revenue, r.costs)));

  const legend = (
    <div className="legend">
      <span className="it">
        <span className="sw" style={{ background: "var(--c-profit)" }} />
        Profit
      </span>
      <span className="it">
        <span className="sw" style={{ background: "var(--c-costs)" }} />
        Costs
      </span>
      <Chips
        value={view}
        onChange={setView}
        options={[
          { key: "chart", label: "Chart" },
          { key: "table", label: "Table" },
        ]}
      />
    </div>
  );

  return (
    <Card title={`Revenue and profit — ${new Date().getFullYear()}`} right={legend}>
      {!rows.length ? (
        <Empty icon="◱">Nothing collected yet this year</Empty>
      ) : view === "table" ? (
        <Table
          rows={rows}
          keyFn={(r) => r.ym}
          columns={[
            { key: "label", label: "Month", render: (r) => <b className="sm">{r.label}</b> },
            { key: "revenue", label: "Revenue", align: "right", render: (r) => <span className="num">{money(r.revenue, sym)}</span> },
            { key: "costs", label: "Costs", align: "right", render: (r) => <span className="num dim">{money(r.costs, sym)}</span> },
            {
              key: "profit",
              label: "Profit",
              align: "right",
              render: (r) => (
                <b className="num" style={{ color: r.profit >= 0 ? undefined : "var(--danger)" }}>
                  {money(r.profit, sym)}
                </b>
              ),
            },
          ]}
          footer={
            <tr>
              <td><b>Year to date</b></td>
              <td className="right num"><b>{money(totals.revenue, sym)}</b></td>
              <td className="right num"><b>{money(totals.costs, sym)}</b></td>
              <td className="right num">
                <b style={{ color: totals.profit < 0 ? "var(--danger)" : undefined }}>{money(totals.profit, sym)}</b>
              </td>
            </tr>
          }
        />
      ) : (
        <>
          <div className="pnl">
            {rows.map((r, i) => {
              const costsPct = (r.costs / max) * 100;
              const profitPct = (Math.max(0, r.profit) / max) * 100;
              const empty = !r.revenue && !r.costs;
              const barPct = (Math.max(r.revenue, r.costs) / max) * 100;
              // Keep the tooltip inside the card: horizontally at either end of
              // the year, vertically when the bar is too tall to sit above.
              const tipPos = {
                ...(i <= 1 ? { left: 0 } : i >= rows.length - 2 ? { right: 0 } : { left: "50%", transform: "translateX(-50%)" }),
                ...(barPct > 58 ? { top: 0 } : { bottom: `calc(${barPct}% + 8px)` }),
              };

              return (
                <div
                  key={r.ym}
                  className="col"
                  tabIndex={0}
                  aria-label={`${r.label}: revenue ${money(r.revenue, sym)}, costs ${money(r.costs, sym)}, ${
                    r.profit >= 0 ? "profit" : "loss"
                  } ${money(Math.abs(r.profit), sym)}`}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                >
                  {empty ? (
                    <span className="none" />
                  ) : (
                    <>
                      {r.costs > 0 && (
                        <span
                          className={`seg costs ${profitPct > 0 ? "" : "cap"}`}
                          style={{ height: `${costsPct}%` }}
                        />
                      )}
                      {profitPct > 0 && (
                        <span
                          className="seg profit"
                          style={{
                            height: `${profitPct}%`,
                            // 2px of surface between the two fills, unless there
                            // are no costs — then profit sits on the baseline.
                            bottom: r.costs > 0 ? `calc(${costsPct}% + 2px)` : 0,
                          }}
                        />
                      )}
                      {r.profit < 0 && r.revenue > 0 && (
                        <span className="rev-line" style={{ bottom: `${(r.revenue / max) * 100}%` }} />
                      )}
                    </>
                  )}

                  {hover === i && (
                    <div className="pnl-tip" style={tipPos}>
                      <div className="m">{r.label}</div>
                      <div className="r">
                        <span className="k" style={{ background: "var(--c-profit)" }} />
                        <span className="lbl">{r.profit >= 0 ? "Profit" : "Loss"}</span>
                        <span className="v">{money(Math.abs(r.profit), sym)}</span>
                      </div>
                      <div className="r">
                        <span className="k" style={{ background: "var(--c-costs)" }} />
                        <span className="lbl">Costs</span>
                        <span className="v">{money(r.costs, sym)}</span>
                      </div>
                      <div className="r">
                        <span className="k" style={{ background: "transparent" }} />
                        <span className="lbl">Revenue</span>
                        <span className="v">{money(r.revenue, sym)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="axis">
            {rows.map((r) => (
              <div key={r.ym}>{r.label}</div>
            ))}
          </div>
          <Sep />
          <div className="row">
            <div>
              <div className="tiny muted">Collected year to date</div>
              <div style={{ fontSize: 18, fontWeight: 650 }}>{money0(totals.revenue, sym)}</div>
            </div>
            <div className="grow" />
            <div className="right">
              <div className="tiny muted">Profit year to date</div>
              {/* Ink, not the series colour — a number is text, and painting it
                  green here would read as a third series. Losses stay flagged. */}
              <div style={{ fontSize: 18, fontWeight: 650, color: totals.profit < 0 ? "var(--danger)" : undefined }}>
                {money0(totals.profit, sym)}
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------- business summary */

function Summary({ data, revenue, sym }) {
  const collected = Number(data.income?.collected_ytd || 0);
  const rebilled = Number(data.rebilled || 0);
  const income = collected + rebilled;
  const costs = data.costs || [];
  const costTotal = costs.reduce((s, c) => s + Number(c.amount || 0), 0);
  const net = income - costTotal;

  return (
    <>
      <MonthlyPnL months={data.months || []} sym={sym} />

      <Grid cols="1fr 1fr" className="mt" style={{ alignItems: "start" }}>
        <Card title="Profit & loss (cash basis)">
          <table>
            <tbody>
              <tr>
                <td>Revenue collected this year</td>
                <td className="right num">{money(collected, sym)}</td>
              </tr>
              <tr>
                <td>Rebilled expenses</td>
                <td className="right num">{money(rebilled, sym)}</td>
              </tr>
              <tr>
                <td>
                  <b>Total income</b>
                </td>
                <td className="right num">
                  <b>{money(income, sym)}</b>
                </td>
              </tr>
              {costs.length ? (
                costs.map((c) => (
                  <tr key={c.category}>
                    <td className="dim">{c.category}</td>
                    <td className="right num dim">({money(c.amount, sym)})</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="dim">No expenses recorded this year</td>
                  <td className="right num dim">—</td>
                </tr>
              )}
              <tr>
                <td>
                  <b>Net</b>
                </td>
                <td className="right num">
                  <b style={{ color: net >= 0 ? "var(--ok)" : "var(--danger)" }}>{money(net, sym)}</b>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="tiny muted" style={{ marginTop: 10 }}>
            Cash basis: revenue is money actually received this year; costs are expenses dated this year plus
            contractor bills you have paid this year, both including the tax you paid, so income and costs measure the
            same thing. Rebilled expenses are counted across all invoices, so they are not limited to this year.
          </div>
        </Card>

        <Card title="Revenue by quarter">
          {data.quarters?.length ? (
            <BarList
              rows={data.quarters.map((q) => ({ label: q.label, value: q.amount }))}
              format={(v) => money(v, sym)}
            />
          ) : (
            <Empty icon="◱">No payments to chart yet</Empty>
          )}
          <Sep />
          <div className="row">
            <div>
              <div className="tiny muted">Effective hourly rate</div>
              <div style={{ fontSize: 18, fontWeight: 650 }}>{money(data.effective?.effective_rate, sym)}</div>
            </div>
            <div className="grow" />
            <div className="right">
              <div className="tiny muted">Utilisation</div>
              <div style={{ fontSize: 18, fontWeight: 650 }}>{Number(data.effective?.utilisation || 0)}%</div>
            </div>
          </div>
          <div className="tiny muted" style={{ marginTop: 10 }}>
            Both figures cover time logged this year.
          </div>
        </Card>
      </Grid>

      <Card className="mt" bare title="Revenue by client">
        <Async state={revenue}>
          {(r) => {
            const rows = r?.rows || [];
            const max = Math.max(1, ...rows.map((x) => Number(x.invoiced || 0)));
            return (
              <Table
                rows={rows}
                onRowClick={(x) => navigate(`/clients/${x.id}`)}
                empty={<Empty icon="◱">Nothing invoiced yet</Empty>}
                columns={[
                  { key: "name", label: "Client", render: (x) => <b>{x.name}</b> },
                  {
                    key: "invoiced",
                    label: "Invoiced",
                    align: "right",
                    render: (x) => <span className="num">{money(x.invoiced, sym)}</span>,
                  },
                  {
                    key: "collected",
                    label: "Collected",
                    align: "right",
                    render: (x) => <span className="num">{money(x.collected, sym)}</span>,
                  },
                  {
                    key: "outstanding",
                    label: "Outstanding",
                    align: "right",
                    render: (x) =>
                      Number(x.outstanding || 0) ? (
                        <span className="num" style={{ color: "var(--danger)" }}>
                          {money(x.outstanding, sym)}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      ),
                  },
                  {
                    key: "hours",
                    label: "Hours",
                    align: "right",
                    render: (x) => <span className="num dim">{Number(x.hours || 0).toFixed(1)}</span>,
                  },
                  {
                    key: "rate",
                    label: "Eff. rate",
                    align: "right",
                    render: (x) =>
                      Number(x.hours || 0) ? (
                        <span className="num dim">{money(Number(x.invoiced) / Number(x.hours), sym)}</span>
                      ) : (
                        <span className="muted">—</span>
                      ),
                  },
                  {
                    key: "share",
                    label: "Share",
                    width: 150,
                    render: (x) => <Bar pct={(Number(x.invoiced || 0) / max) * 100} />,
                  },
                ]}
              />
            );
          }}
        </Async>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------- a/r ageing */

const BUCKETS = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30" },
  { key: "d31_60", label: "31–60" },
  { key: "d60_plus", label: "60+" },
];

function Ageing({ rows, sym }) {
  const sum = (key) => rows.reduce((s, r) => s + Number(r[key] || 0), 0);
  const totals = { current: sum("current"), d1_30: sum("d1_30"), d31_60: sum("d31_60"), d60_plus: sum("d60_plus") };
  const grand = totals.current + totals.d1_30 + totals.d31_60 + totals.d60_plus;
  const pastDue = totals.d1_30 + totals.d31_60 + totals.d60_plus;
  const lateClients = rows.filter((r) => Number(r.current || 0) < Number(r.total || 0)).length;

  const cell = (v, i) => {
    const n = Number(v || 0);
    if (!n) return <span className="muted">—</span>;
    return (
      <span className="num" style={i > 1 ? { color: "var(--danger)", fontWeight: 600 } : undefined}>
        {money(n, sym)}
      </span>
    );
  };

  return (
    <>
      <Card
        bare
        title="Accounts receivable ageing"
        right={<span className="sm muted">As at {fmtDate(today())}</span>}
      >
        <Table
          rows={rows}
          onRowClick={(r) => navigate(`/clients/${r.id}`)}
          empty={<Empty icon="◱">Nothing outstanding — every invoice is settled</Empty>}
          columns={[
            { key: "name", label: "Client", render: (r) => <b>{r.name}</b> },
            ...BUCKETS.map((b, i) => ({
              key: b.key,
              label: b.label,
              align: "right",
              render: (r) => cell(r[b.key], i),
            })),
            {
              key: "total",
              label: "Total",
              align: "right",
              render: (r) => <b className="num">{money(r.total, sym)}</b>,
            },
          ]}
          footer={
            rows.length ? (
              <tr>
                <td>
                  <b>Total</b>
                </td>
                {BUCKETS.map((b) => (
                  <td key={b.key} className="right num">
                    <b>{money(totals[b.key], sym)}</b>
                  </td>
                ))}
                <td className="right num">
                  <b>{money(grand, sym)}</b>
                </td>
              </tr>
            ) : null
          }
        />
      </Card>

      {pastDue > 0 && (
        <div className="mt">
          <Ribbon tone="warn">
            ⚠ {money(pastDue, sym)} is past due across {lateClients} client{lateClients === 1 ? "" : "s"}.
          </Ribbon>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------- sales tax */

function SalesTax({ data, sym, label }) {
  return (
    <Card title={`${label} summary — this quarter to date`}>
      <table>
        <tbody>
          <tr>
            <td>{label} collected on sales</td>
            <td className="right num">{money(data.collected, sym)}</td>
          </tr>
          <tr>
            <td>Input tax credits on expenses</td>
            <td className="right num">({money(data.credits_expenses ?? data.credits, sym)})</td>
          </tr>
          <tr>
            <td>Input tax credits on contractor bills</td>
            <td className="right num">({money(data.credits_bills, sym)})</td>
          </tr>
          <tr>
            <td>
              <b>Net {label} payable</b>
            </td>
            <td className="right num">
              <b>{money(data.net, sym)}</b>
            </td>
          </tr>
        </tbody>
      </table>
      <Sep />
      <div className="tiny muted">
        Counts sent invoices issued in the current quarter, expenses dated in it, and contractor bills paid in it. One
        tax rate per invoice, so if you bill across provinces or need multiple tax codes per line, reconcile this
        against your filing before submitting it.
      </div>
    </Card>
  );
}
